/**
 * Capture the Flag: the two flags and their carriers, the off-screen arrows,
 * the minimap markers, the HUD state and the match result. Legacy kept this
 * in Games.js.
 */
import type { ServerCustomCTFData, ServerGameFlagMessage } from '@airmash/protocol';
import { CTF_FLAG_STATE, CTF_TEAMS } from '@airmash/protocol';
import type { Sprite } from 'pixi.js';
import { uiState } from '../ui/UIState';
import { clock } from './Clock';
import {
  getCamera as graphicsGetCamera,
  inScreen as graphicsInScreen,
  minimapMob as graphicsMinimapMob,
  shadowCoords as graphicsShadowCoords,
  viewport,
} from './Graphics';
import { notifyCtfMatchEnded, startCtfMatchTimer } from './MatchTimer';
import { get as playersGet } from './Players';
import { session } from './Session';
import { gameComplete as soundGameComplete } from './Sound';
import { init as texturesInit } from './Textures';
import { clamp as toolsClamp } from './Tools';
import { minimapLayout } from './UI';
import Vector from './Vector';

/** A team's flag: where it is, who carries it, its flutter and its sprites. */
interface CtfFlag {
  visible: boolean;
  /** The carrier's id; null while the flag rests at its base or lies dropped. */
  playerId: number | null;
  /** The flutter while carried: the sprite's x scale swings with the carrier's sideways motion. */
  direction: number;
  diffX: number;
  momentum: number;
  position: Vector;
  basePos: Vector;
  sprite: Sprite;
  spriteShadow: Sprite;
  minimapSprite: Sprite;
  minimapBase: Sprite;
  arrowSprite: Sprite;
}

/** What differs between the teams. */
interface CtfTeam {
  /** The team's key in the HUD state. */
  side: 'blue' | 'red';
  /** The suffix of the team's texture names. */
  textureSuffix: 'Blue' | 'Red';
  /** The team's score field in a GAME_FLAG packet. */
  scoreField: 'blueteam' | 'redteam';
  /** Where the team's flag rests. The server never sends the bases. */
  base: Vector;
}

const TEAMS: Readonly<Record<CTF_TEAMS, CtfTeam>> = {
  [CTF_TEAMS.BLUE]: { side: 'blue', textureSuffix: 'Blue', scoreField: 'blueteam', base: new Vector(-9669, -1471) },
  [CTF_TEAMS.RED]: { side: 'red', textureSuffix: 'Red', scoreField: 'redteam', base: new Vector(8602, -944) },
};

/** Both flags by team, present only while a CTF game is prepared. */
let flags: Record<CTF_TEAMS, CtfFlag> | null = null;

/** World scale of a flag sprite; its shadow is a little larger. */
const FLAG_SCALE = 0.4;
const SHADOW_SCALE_RATIO = 1.1;
const FLAG_SHADOW_SCALE = FLAG_SCALE * SHADOW_SCALE_RATIO;

/** A resting flag draws within this many world units of the screen edge. */
const FLAG_DRAW_MARGIN = 128;

/**
 * Squared distance in world units within which a flag counts as at its base.
 * The server's at-base coordinates are a unit or so off ours and carried
 * positions drift, so equality would leave the arrow on at join; an 8-unit
 * radius is far smaller than a plane.
 */
const AT_BASE_RADIUS_SQ = 64;

/** The off-screen arrow sits this many pixels inside the screen edge; a flag within that band needs none. */
const ARROW_MARGIN = 65;

/**
 * The carried flag's flutter: the carrier's sideways motion builds momentum up
 * to a cap, the sprite's x scale swings a step per frame between the limits,
 * and the sprite leans with the motion.
 */
const FLUTTER_MOMENTUM_MAX = 40;
const FLUTTER_DIRECTION_STEP = 0.1;
const FLUTTER_DIRECTION_MAX = 0.4;
const FLUTTER_LEAN = 0.04;

/** The minimap flag and base sprites' scale at minimap zoom 1. */
const MINIMAP_FLAG_SCALE = 0.5 * 0.75;

/**
 * The minimap flag and base sprites follow the minimap's zoom. Applied at
 * creation as well as on resize; flags created on join once rendered at
 * texture scale until the first resize.
 */
function minimapFlagScale(): number {
  return MINIMAP_FLAG_SCALE * minimapLayout.scale;
}

/** Builds a team's flag, at its base so the arrow's at-base check holds before the first GAME_FLAG packet. */
function createFlag(team: CTF_TEAMS): CtfFlag {
  const { textureSuffix, base } = TEAMS[team];
  const basePos = base.clone();
  return {
    visible: false,
    playerId: null,
    direction: 1,
    diffX: 0,
    momentum: 0,
    position: basePos.clone(),
    basePos,
    sprite: texturesInit(`ctfFlag${textureSuffix}`, { scale: FLAG_SCALE, visible: false }),
    spriteShadow: texturesInit('ctfFlagShadow', { scale: FLAG_SHADOW_SCALE, visible: false }),
    minimapSprite: texturesInit(`minimapFlag${textureSuffix}`, { scale: minimapFlagScale() }),
    minimapBase: texturesInit(`minimapBase${textureSuffix}`, { scale: minimapFlagScale() }),
    arrowSprite: texturesInit(`ctfArrow${textureSuffix}`, { visible: false }),
  };
}

/** Prepares a CTF game: the HUD state, both flags at their bases, and the match clock. */
export function prep(): void {
  uiState.ctfHud = {
    blue: { carrierName: null, captures: 0 },
    red: { carrierName: null, captures: 0 },
  };

  flags = { [CTF_TEAMS.BLUE]: createFlag(CTF_TEAMS.BLUE), [CTF_TEAMS.RED]: createFlag(CTF_TEAMS.RED) };

  // The sprites start at the base like `position`, or the world ones would
  // show at the map's centre and the minimap ones at the canvas corner until
  // the first GAME_FLAG packet moved them
  for (const flag of Object.values(flags)) {
    flag.sprite.position.set(flag.basePos.x, flag.basePos.y);
    const baseShadow = graphicsShadowCoords(flag.basePos);
    flag.spriteShadow.position.set(baseShadow.x, baseShadow.y);
    graphicsMinimapMob(flag.minimapSprite, flag.basePos.x, flag.basePos.y);
    graphicsMinimapMob(flag.minimapBase, flag.basePos.x, flag.basePos.y);
  }

  // The elapsed-time HUD: anchored on the local clock, with a /match query on join for a game in progress
  startCtfMatchTimer();
}

/**
 * Clears the CTF state and destroys the flag sprites; Pixi 8's destroy takes
 * each one off its layer. Safe to call in any game mode.
 */
export function wipe(): void {
  uiState.ctfHud = null;
  // A carrier who switches game would otherwise keep the mobile DROP button
  uiState.isFlagCarrier = false;
  if (flags === null) return;
  for (const flag of Object.values(flags)) {
    flag.sprite.destroy();
    flag.spriteShadow.destroy();
    flag.minimapSprite.destroy();
    flag.minimapBase.destroy();
    flag.arrowSprite.destroy();
  }
  flags = null;
}

/** Handles GAME_FLAG: a flag resting or dropped at a position, or carried by a player, with both teams' scores. */
export function handleFlag(msg: ServerGameFlagMessage): void {
  if (flags === null) return;

  const flag = flags[msg.flag];
  const team = TEAMS[msg.flag];

  // The flutter restarts
  flag.momentum = 0;
  flag.direction = 1;
  flag.sprite.scale.x = FLAG_SCALE;
  flag.sprite.rotation = 0;
  flag.spriteShadow.scale.x = FLAG_SHADOW_SCALE;
  flag.spriteShadow.rotation = 0;

  let carrierName: string | null = null;
  if (msg.type === CTF_FLAG_STATE.STATIC) {
    flag.playerId = null;
    flag.position.x = msg.posX;
    flag.position.y = msg.posY;
    flag.sprite.position.set(msg.posX, msg.posY);
    const shadow = graphicsShadowCoords(flag.position);
    flag.spriteShadow.position.set(shadow.x, shadow.y);
    graphicsMinimapMob(flag.minimapSprite, msg.posX, msg.posY);
  } else {
    flag.playerId = msg.id;
    const carrier = playersGet(msg.id);
    if (carrier !== undefined) {
      carrierName = carrier.name;
      flag.diffX = carrier.pos.x;
    }
  }

  if (uiState.ctfHud !== null) {
    const hudFlag = uiState.ctfHud[team.side];
    hudFlag.captures = msg[team.scoreField];
    hudFlag.carrierName = carrierName;
  }

  // Whether I carry either flag, for the mobile DROP button. GAME_FLAG arrives
  // on every take, drop, capture and return, including the drop when a carrier
  // dies, so this stays current without polling; the guard keeps it a no-op
  // when unchanged.
  const carrying =
    session.myID !== null &&
    (flags[CTF_TEAMS.BLUE].playerId === session.myID || flags[CTF_TEAMS.RED].playerId === session.myID);
  if (uiState.isFlagCarrier !== carrying) {
    uiState.isFlagCarrier = carrying;
  }

  updateFlag(flag, false);
}

/**
 * Moves a flag with its carrier or shows it when it comes on screen, keeps its
 * minimap marker and arrow current, and rescales the minimap sprites on a resize.
 */
function updateFlag(flag: CtfFlag, isResize: boolean): void {
  if (isResize) {
    graphicsMinimapMob(flag.minimapSprite, flag.position.x, flag.position.y);
    graphicsMinimapMob(flag.minimapBase, flag.basePos.x, flag.basePos.y);
    const scale = minimapFlagScale();
    flag.minimapSprite.scale.set(scale);
    flag.minimapBase.scale.set(scale);
  }

  if (flag.playerId !== null) {
    const carrier = playersGet(flag.playerId);
    if (carrier !== undefined) {
      // The flag shows with its carrier
      if (carrier.render !== flag.visible) {
        flag.visible = carrier.render;
        flag.sprite.visible = carrier.render;
        flag.spriteShadow.visible = carrier.render;
        if (carrier.render) {
          flag.momentum = 0;
          flag.direction = 1;
          flag.diffX = carrier.pos.x;
        }
      }

      // The minimap marker follows the carrier's exact position on screen and the low-resolution one off it
      if (carrier.render) {
        graphicsMinimapMob(flag.minimapSprite, carrier.pos.x, carrier.pos.y);
      } else {
        graphicsMinimapMob(flag.minimapSprite, carrier.lowResPos.x, carrier.lowResPos.y);
      }

      if (flag.visible) {
        flag.position.x = carrier.pos.x;
        flag.position.y = carrier.pos.y;
        flag.sprite.position.set(carrier.pos.x, carrier.pos.y);
        const shadow = graphicsShadowCoords(carrier.pos);
        flag.spriteShadow.position.set(shadow.x, shadow.y);

        // The flutter
        flag.momentum = toolsClamp(
          flag.momentum + (carrier.pos.x - flag.diffX) * clock.timeFactor,
          -FLUTTER_MOMENTUM_MAX,
          FLUTTER_MOMENTUM_MAX,
        );
        const directionStep = flag.momentum > 0 ? FLUTTER_DIRECTION_STEP : -FLUTTER_DIRECTION_STEP;
        flag.direction = toolsClamp(
          flag.direction - directionStep * clock.timeFactor,
          -FLUTTER_DIRECTION_MAX,
          FLUTTER_DIRECTION_MAX,
        );
        flag.sprite.scale.x = flag.direction;
        flag.spriteShadow.scale.x = SHADOW_SCALE_RATIO * flag.direction;
        const lean = FLUTTER_LEAN * -(carrier.pos.x - flag.diffX) * clock.timeFactor;
        flag.sprite.rotation = lean;
        flag.spriteShadow.rotation = lean;
        flag.diffX = carrier.pos.x;
      }
    }
  } else {
    // A resting flag shows when on screen
    const isVisible = graphicsInScreen(flag.position, FLAG_DRAW_MARGIN);
    if (isVisible !== flag.visible) {
      flag.visible = isVisible;
      flag.sprite.visible = isVisible;
      flag.spriteShadow.visible = isVisible;
    }
  }

  updateArrow(flag);
}

/** Points an arrow at the screen edge towards a flag that is off screen and away from its base. */
function updateArrow(flag: CtfFlag): void {
  // None for the flag I carry
  if (flag.playerId === session.myID) {
    flag.arrowSprite.visible = false;
    return;
  }

  // Where the flag is: with its carrier, even off screen
  let flagX = flag.position.x;
  let flagY = flag.position.y;
  if (flag.playerId !== null) {
    const carrier = playersGet(flag.playerId);
    if (carrier !== undefined) {
      flagX = carrier.render ? carrier.pos.x : carrier.lowResPos.x;
      flagY = carrier.render ? carrier.pos.y : carrier.lowResPos.y;
    }
  }

  // None for a flag at its base
  const dxBase = flagX - flag.basePos.x;
  const dyBase = flagY - flag.basePos.y;
  if (dxBase * dxBase + dyBase * dyBase < AT_BASE_RADIUS_SQ) {
    flag.arrowSprite.visible = false;
    return;
  }

  // None for a flag on screen, inside the margin band
  const camera = graphicsGetCamera();
  const screenX = (flagX - camera.x) * viewport.scale + viewport.halfWidth;
  const screenY = (flagY - camera.y) * viewport.scale + viewport.halfHeight;
  if (
    screenX > ARROW_MARGIN &&
    screenX < viewport.width - ARROW_MARGIN &&
    screenY > ARROW_MARGIN &&
    screenY < viewport.height - ARROW_MARGIN
  ) {
    flag.arrowSprite.visible = false;
    return;
  }

  // The arrow sits where the line from the screen's centre to the flag leaves
  // the margin rectangle: the offset scaled down to the nearer edge. The UI
  // layer is in screen space.
  const relX = screenX - viewport.halfWidth;
  const relY = screenY - viewport.halfHeight;
  const halfWidth = (viewport.width - ARROW_MARGIN * 2) / 2;
  const halfHeight = (viewport.height - ARROW_MARGIN * 2) / 2;
  const edgeScale = Math.min(halfWidth / Math.abs(relX), halfHeight / Math.abs(relY));
  flag.arrowSprite.visible = true;
  flag.arrowSprite.position.set(viewport.halfWidth + relX * edgeScale, viewport.halfHeight + relY * edgeScale);
  flag.arrowSprite.rotation = Math.atan2(relX, -relY);
}

/** Handles the SERVER_CUSTOM CTF message: the match result, shown once per match, and the clock freeze. */
export function showWin(msg: ServerCustomCTFData): void {
  // Only the first result shows; the panel dismisses itself after the duration
  if (uiState.ctfMatchResult !== null) return;

  // Spectators keep a team, so they get their team's result, as in StarMash
  const isVictory = session.myTeam === null ? null : msg.w === session.myTeam;
  uiState.ctfMatchResult = {
    isVictory,
    winnerTeam: msg.w === CTF_TEAMS.BLUE ? 1 : 2,
    bounty: msg.b,
    duration: msg.t,
  };

  soundGameComplete();
  // Freeze the elapsed-time HUD on the final match length; the next
  // "Game starting!" will reset it to 0:00.
  notifyCtfMatchEnded();
}

/** Frame update: both flags follow their carriers or the camera. */
export function update(isResize: boolean): void {
  if (flags === null) return;
  updateFlag(flags[CTF_TEAMS.BLUE], isResize);
  updateFlag(flags[CTF_TEAMS.RED], isResize);
}
